import { Component, OnInit, Input } from '@angular/core';
import * as _ from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GfTitleService } from '../../../../services/core.title.service';
import { ProjectService } from '../../../../services/project.service';
import { SearchService } from '../../../../services/core.search.service';
import { UiService } from '../../../../services/core.ui.service';
import { ProjectModel } from '../../../../services/project.model.service';
import { StateService } from '@uirouter/angular';
import { ProjectAdminOrgRelationDeleteComponent } from './project.admin.orgs.relation.delete.modal'

@Component({
  selector: 'project-admin-orgs',
  template: require('./project.admin.orgs.html')
})
export class ProjectAdminOrgsComponent implements OnInit {
  @Input() project: any;
  @Input() isProjectAdmin: any;

  childSearch: string = '';
  parentSearch: string = '';
  children: any;
  childrenLoading: boolean = false;
  parent: any;
  parentLoading: boolean = false;
  searchingParents: boolean = false;
  searchingChildren: boolean = false;
  parentOpts: any[] = [];
  childOpts: any[] = [];
  targetParent: any;
  targetChild: any;
  targetType: string;
  targetName: any;
  projectStatus: any;
  focusParent: any;

  // Debounced search functions
  doSearchParents: () => void;
  doSearchChildren: () => void;

  constructor(
    private titleService: GfTitleService,
    private projectService: ProjectService,
    private searchService: SearchService,
    private uiService: UiService,
    private projectModel: ProjectModel,
    private stateService: StateService, 
    
    private modalService: NgbModal
  ) {
    this.doSearchParents = _.debounce(() => {
      if (this.parentSearch) {
        this.searchService.searchProjects({
          page_size: 99,
          q: this.parentSearch,
          canParent: this.project.id
        })
          .then((ret: any) => {
            this.parentOpts = ret.items;
            this.searchingParents = false;
          })
          .catch(() => {
            this.parentOpts = [];
            this.searchingParents = false;
          });
      } else {
        this.parentOpts = [];
        this.searchingParents = false;
      }
    }, 1000);

    this.doSearchChildren = _.debounce(() => {
      if (this.childSearch) {
        this.searchService.searchProjects({
          page_size: 99,
          q: this.childSearch,
          canChild: this.project.id
        })
          .then((ret: any) => {
            this.childOpts = ret.items;
            this.searchingChildren = false;
          })
          .catch(() => {
            this.childOpts = [];
            this.searchingChildren = false;
          });
      } else {
        this.childOpts = [];
        this.searchingChildren = false;
      }
    }, 1000);
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.project.name, 'Admin', 'Orgs');
    if (this.project) {
      this.getChildren();
      this.parent = this.project?.rel?.parent;
      this.projectStatus = this.projectModel.getStatusDescription(this.project);
    }
  }

  ngOnChanges(): void {
    if (this.project) {
      this.getChildren();
      this.parent = this.project?.rel?.parent;
      this.projectStatus = this.projectModel.getStatusDescription(this.project);
    }
  }

  getProjectStatus(project: any): any {
    return this.projectModel.getStatusDescription(project);
  }

  getChildren(): void {
    this.childrenLoading = true;
    this.projectService.fetch({ page_size: 100, parentProjectId: this.project.id })
      .then((ret: any) => {
        this.children = ret.items;
        this.childrenLoading = false;
      })
      .catch(() => {
        this.childrenLoading = false;
      });
  }

  getParent(id: any): void {
    if (id > 0) {
      this.parentLoading = true;
      this.projectService.details(id)
        .then(ret => {
          this.parent = ret;
          this.parentLoading = false;
        })
        .catch(() => {
          this.parent = { unixName: 'You do not have permission to view parent' };
          this.parentLoading = false;
        });
    } else {
      this.parent = null;
    }
  }

  searchParents(): void {
    this.searchingParents = true;
    this.doSearchParents();
  }

  searchChildren(): void {
    this.searchingChildren = true;
    this.doSearchChildren();
  }

  focusParentMethod(value: any): void {
    this.focusParent = value;
  }

  setParent(parentId: any, childId: any, updateType: string): void {
    if (parentId === 0 || childId === 0) {
      this.targetParent = parentId;
      this.targetChild = childId;
      this.targetType = updateType;
      this.targetName = parentId || childId;
    } else {
      this.projectService.setParent(childId, parentId)
        .then(() => {
          this.uiService.message.showSuccess('Updated ' + updateType);
          if (updateType === 'parent') {
            this.parentOpts = [];
            this.searchingParents = false;
            this.parentSearch = '';
            this.getParent(parentId);
            if (childId === this.project.id) {
              if (!this.project.rel) {
                this.project.rel = {};
              }
              this.projectService.details(parentId)
                .then((parentDetails: any) => {
                  this.parent = parentDetails;
                  this.project.rel.parent = parentDetails.id;
                })
                .catch(() => {
                  this.uiService.message.showWarning('Parent was set but could not be displayed. Please refresh.');
                });
            }
          } else if (updateType === 'child') {
            this.childOpts = [];
            this.searchingChildren = false;
            this.childSearch = '';
            this.getChildren();
          }
        })
        .catch(result => {
          const msg = _.get(result, 'message', 'An error occurred.');
          this.uiService.message.showWarning('Failed to update ' + updateType + ': ' + msg);
        });
    }
  }

  openRelationDeleteModal(parentProject: any, childProject: any, relationType: string): void {
    const modalRef = this.modalService.open(ProjectAdminOrgRelationDeleteComponent);
    modalRef.componentInstance.parentProject = parentProject;
    modalRef.componentInstance.childProject = childProject;
    modalRef.componentInstance.relationType = relationType;

    modalRef.componentInstance.onClose.subscribe(() => {
        modalRef.close();
    })
    modalRef.componentInstance.onDelete.subscribe((data) => {
      this.projectService.setParent(data.childId, null)  // Pass null to remove the relationship
        .then(() => {
          this.uiService.message.showSuccess('Project relationship removed successfully');
          if (data.type === 'parent') {
            this.parent = null;
          } else if (data.type === 'child') {
            this.getChildren();
          }
        })
        .catch(result => {
          const msg = _.get(result, 'message', 'An error occurred.');
          this.uiService.message.showWarning('Failed to remove relationship: ' + msg);
        });
      modalRef.close();
    });
  }
}
